import { useState, useEffect } from 'react';
import { publicApiService } from '../services/publicApi';
import { SiteInfo, Projet, GalerieMedia, BatimentConfigurationPublic, HeroSlider, Actualite, ApiResponse } from '../types/api';

// ===== SITE INFO HOOKS =====

/**
 * Hook pour récupérer toutes les informations du site
 */
export const useSiteInfos = (params?: {
  type_info?: string;
  search?: string;
  ordering?: string;
}) => {
  const [data, setData] = useState<SiteInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await publicApiService.getSiteInfos(params);
        setData(response.results || []);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Erreur lors du chargement des informations');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [params?.type_info, params?.search, params?.ordering]);

  return { data, loading, error };
};

/**
 * Hook pour récupérer les informations par type
 */
export const useSiteInfosByType = (typeInfo: string) => {
  const [data, setData] = useState<SiteInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await publicApiService.getSiteInfosByType(typeInfo);
        setData(response);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Erreur lors du chargement des informations');
      } finally {
        setLoading(false);
      }
    };

    if (typeInfo) {
      fetchData();
    }
  }, [typeInfo]);

  return { data, loading, error };
};

/**
 * Hook pour récupérer les données de la page d'accueil
 */
export const useHomeData = () => {
  const [data, setData] = useState<{
    home: SiteInfo[];
    contact: SiteInfo[];
    social: SiteInfo[];
  }>({ home: [], contact: [], social: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await publicApiService.getHomeData();
        setData(response);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return { data, loading, error };
};

// ===== PROJET HOOKS =====

/**
 * Hook pour récupérer tous les projets
 */
export const useProjets = (params?: {
  statut_projet?: string;
  promoteur?: string;
  search?: string;
  ordering?: string;
}) => {
  const [data, setData] = useState<Projet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await publicApiService.getProjets(params);
        setData(response);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Erreur lors du chargement des projets');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [params?.statut_projet, params?.promoteur, params?.search, params?.ordering]);

  return { data, loading, error };
};

/**
 * Hook pour récupérer un projet spécifique
 */
export const useProjet = (id: number | null) => {
  const [data, setData] = useState<Projet | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const response = await publicApiService.getProjet(id);
        setData(response);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Erreur lors du chargement du projet');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  return { data, loading, error };
};

/**
 * Hook pour récupérer la galerie d'un projet
 */
export const useProjetGalerie = (id: number | null) => {
  const [data, setData] = useState<GalerieMedia[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const response = await publicApiService.getProjetGalerie(id);
        setData(response);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Erreur lors du chargement de la galerie');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  return { data, loading, error };
};

// ===== CONVENIENCE HOOKS =====

/**
 * Hook pour récupérer les informations de contact
 */
export const useContactInfo = () => {
  return useSiteInfosByType('CONTACT');
};

/**
 * Hook pour récupérer les liens des réseaux sociaux
 */
export const useSocialLinks = () => {
  return useSiteInfosByType('SOCIAL');
};

/**
 * Hook pour récupérer les informations de la page d'accueil
 */
export const useHomeInfo = () => {
  return useSiteInfosByType('HOME');
};

/**
 * Hook pour récupérer les informations légales
 */
export const useLegalInfo = () => {
  return useSiteInfosByType('LEGAL');
};

/**
 * Hook pour récupérer les informations "À propos"
 */
export const useAboutInfo = () => {
  return useSiteInfosByType('ABOUT');
};

/**
 * Hook pour récupérer les employés à afficher sur la page À propos
 */
export const useEmployes = () => {
  const [data, setData] = useState<Array<{
    id: number;
    full_name: string;
    fonction: string;
    image_url: string | null;
    ordre: number;
  }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await publicApiService.getEmployes();
        setData(response);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Erreur lors du chargement des employés');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return { data, loading, error };
};

/**
 * Hook pour récupérer les statistiques d'un projet
 */
export const useProjetStatistiques = (id: number | null) => {
  const [data, setData] = useState<{
    total_batiments: number;
    types_maison_total: number;
    total_logements: number;
    logements_disponibles: number;
    souscriptions_total: number;
    revenus_total: number;
    surface_totale: number;
    budget_total: number;
    taux_occupation: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const response = await publicApiService.getProjetStatistiques(id);
        setData(response);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Erreur lors du chargement des statistiques');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  return { data, loading, error };
};

/**
 * Hook pour récupérer les configurations de maisons disponibles pour un projet (nouveau système uniquement)
 */
export const useProjetTypesMaisons = (id: number | null) => {
  const [data, setData] = useState<BatimentConfigurationPublic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const response = await publicApiService.getProjetMaisonsDisponibles(id);
        setData(response);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Erreur lors du chargement des types de maisons');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  return { data, loading, error };
};

/**
 * Hook pour récupérer les statistiques globales de tous les projets
 */
export const useGlobalStatistiques = () => {
  const [data, setData] = useState<{
    total_projets: number;
    projets_en_cours: number;
    projets_termines: number;
    total_logements: number;
    total_surface: number;
    total_souscriptions: number;
    total_revenus: number;
    taux_occupation_global: number;
    logements_disponibles: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await publicApiService.getGlobalStatistiques();
        setData(response);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Erreur lors du chargement des statistiques');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return { data, loading, error };
};

/**
 * Hook pour récupérer les contenus statiques publiés (pour le slider hero)
 */
export const useHeroSliders = () => {
  const [data, setData] = useState<HeroSlider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await publicApiService.getHeroSliders();
        setData(response.results || []);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Erreur lors du chargement des slides');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return { data, loading, error };
};

/**
 * Hook pour récupérer les actualités publiques
 */
export const useActualites = (params?: {
  projet?: number;
  afficher_accueil?: boolean;
  search?: string;
  ordering?: string;
  page?: number;
}) => {
  const [data, setData] = useState<Actualite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<{
    count: number;
    next?: string;
    previous?: string;
  } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await publicApiService.getActualites(params);
        if (Array.isArray((response as any).results)) {
          const paginated = response as ApiResponse<Actualite>;
          setData(paginated.results);
          if (typeof paginated.count === 'number') {
            setMeta({
              count: paginated.count,
              next: paginated.next,
              previous: paginated.previous,
            });
          } else {
            setMeta(null);
          }
        } else if (Array.isArray(response as unknown as Actualite[])) {
          setData(response as unknown as Actualite[]);
          setMeta(null);
        } else {
          setData([]);
          setMeta(null);
        }
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Erreur lors du chargement des actualités');
        setMeta(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    params?.projet,
    params?.afficher_accueil,
    params?.search,
    params?.ordering,
    params?.page,
  ]);

  return { data, loading, error, meta };
};

/**
 * Hook pour récupérer une actualité par son slug
 */
export const useActualite = (slug: string | null) => {
  const [data, setData] = useState<Actualite | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!slug) return;

      try {
        setLoading(true);
        setError(null);
        const response = await publicApiService.getActualite(slug);
        setData(response);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Actualité introuvable');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  return { data, loading, error };
};
